/**
 * stepRecent.ts — "Welcome back" / recent creations screen.
 *
 * Shown between step 0 (intro) and step 1 (base picker) ONLY when the device has
 * saved designs. Left: a yellow "Start fresh" card → new design. Right: the saved
 * designs as cards (horizontal scroll past 3). Each card hovers to reveal Edit
 * Design + Add to bag (or just Edit, with a "some charms sold out" tag, when the
 * piece can't be ordered as-is), plus a delete trash top-right.
 */

import { buildAvailability, type Catalog } from './data';
import type { SavedBuild } from './favorites';
import { makeLoadingButton } from './loadingButton';
import { initGlassGlow, glowColor } from './step1';

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };
const priceLabel = (n: number) => '€' + (Number.isInteger(n) ? n : n.toFixed(2));

// White PaoPao blob for the yellow Start-fresh card.
const PAOPAO_WHITE = `<svg viewBox="0 0 477 477" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full"><path d="M393.551 82.2504C356.197 61.0458 307.1 66.9291 273.518 92.9463C245.969 114.354 237.636 123.896 207.656 98.6581C201.515 93.7571 195.141 88.8562 188.295 84.9219C159.471 68.0465 122.412 63.764 91.6287 77.838C60.3844 92.2343 36.7063 120.85 21.5013 151.321C-17.1848 229.955 17.9152 317.648 89.0427 363.825C133.093 393.688 189.891 408.505 243.937 407.279C346.371 407.268 450.018 342.792 468.136 252.35C476.718 213.444 468.716 170.629 447.608 136.686C434.197 114.93 415.991 94.7394 393.697 82.3285L393.562 82.2504H393.551Z" fill="#f5f5f5"/></svg>`;
const TRASH = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.5 3.5H11V3C11 2.60218 10.842 2.22064 10.5607 1.93934C10.2794 1.65804 9.89782 1.5 9.5 1.5H6.5C6.10218 1.5 5.72064 1.65804 5.43934 1.93934C5.15804 2.22064 5 2.60218 5 3V3.5H2.5C2.36739 3.5 2.24021 3.55268 2.14645 3.64645C2.05268 3.74021 2 3.86739 2 4C2 4.13261 2.05268 4.25979 2.14645 4.35355C2.24021 4.44732 2.36739 4.5 2.5 4.5H3V13C3 13.2652 3.10536 13.5196 3.29289 13.7071C3.48043 13.8946 3.73478 14 4 14H12C12.2652 14 12.5196 13.8946 12.7071 13.7071C12.8946 13.5196 13 13.2652 13 13V4.5H13.5C13.6326 4.5 13.7598 4.44732 13.8536 4.35355C13.9473 4.25979 14 4.13261 14 4C14 3.86739 13.9473 3.74021 13.8536 3.64645C13.7598 3.55268 13.6326 3.5 13.5 3.5ZM6 3C6 2.86739 6.05268 2.74021 6.14645 2.64645C6.24021 2.55268 6.36739 2.5 6.5 2.5H9.5C9.63261 2.5 9.75979 2.55268 9.85355 2.64645C9.94732 2.74021 10 2.86739 10 3V3.5H6V3ZM12 13H4V4.5H12V13ZM6.5 6.5V11C6.5 11.1326 6.44732 11.2598 6.35355 11.3536C6.25979 11.4473 6.13261 11.5 6 11.5C5.86739 11.5 5.74021 11.4473 5.64645 11.3536C5.55268 11.2598 5.5 11.1326 5.5 11V6.5C5.5 6.36739 5.55268 6.24021 5.64645 6.14645C5.74021 6.05268 5.86739 6 6 6C6.13261 6 6.25979 6.05268 6.35355 6.14645C6.44732 6.24021 6.5 6.36739 6.5 6.5ZM10.5 6.5V11C10.5 11.1326 10.4473 11.2598 10.3536 11.3536C10.2598 11.4473 10.1326 11.5 10 11.5C9.86739 11.5 9.74021 11.4473 9.64645 11.3536C9.55268 11.2598 9.5 11.1326 9.5 11V6.5C9.5 6.36739 9.55268 6.24021 9.64645 6.14645C9.74021 6.05268 9.86739 6 10 6C10.1326 6 10.2598 6.05268 10.3536 6.14645C10.4473 6.24021 10.5 6.36739 10.5 6.5Z" fill="currentColor"/></svg>`;

const BTN_PRIMARY   = 'btn btn--primary text-button-text bg-button text-xs px-3 py-2.5 leading-none rounded-xs font-semibold uppercase cursor-pointer flex-1';
const BTN_SECONDARY = 'btn btn--secondary bg-background text-foreground text-xs px-3 py-2.5 leading-none rounded-xs font-semibold uppercase cursor-pointer flex-1 shadow-[inset_0_0_0_1px_var(--color-secondary-button-text)]';

export interface MountRecentOpts {
  root: HTMLElement;
  catalog: Catalog;
  favorites: SavedBuild[];
  onNewDesign: () => void;
  onEdit: (fav: SavedBuild) => void;
  onAddToBag: (fav: SavedBuild) => void | Promise<void>; // awaited for the button loading state
  onDelete: (id: string) => Promise<void>;
}

export function mountRecentCreations(opts: MountRecentOpts): void {
  const { root, catalog, onNewDesign, onEdit, onAddToBag, onDelete } = opts;
  let favorites = opts.favorites;
  root.innerHTML = '';
  initGlassGlow(); // cursor-follow glow for the glass buttons on this screen

  const wrap = el('div', 'cfg cfg-recent relative overflow-hidden bg-background justify-between flex flex-col px-3 md:px-4 pt-18 pb-4 min-h-[640px]');

  // Top copy (name omitted for now — that arrives with accounts).
  const copy = el('div', 'mb-8 md:mb-12');
  const h = el('h1', 'h2 mb-2'); h.textContent = 'Welcome back.';
  const sub = el('p', 'p m-0 text-foreground/70'); sub.textContent = 'Your saved designs are waiting for you.';
  copy.append(h, sub);

  // Start fresh + Recent creations. Mobile: stacked, fresh 2/5 + cards 3/5 of the height.
  // Desktop: two side-by-side columns.
  const cols = el('div', 'flex-1 min-h-0 grid grid-cols-1 grid-rows-[2fr_3fr] gap-6 md:flex-none md:grid-cols-[minmax(280px,1fr)_3fr] md:grid-rows-1 md:gap-8 items-stretch');

  // ── Start fresh ──
  const fresh = el('div', 'flex flex-col min-h-0');
  const freshLabel = el('div', 'h4 uppercase mb-1.5 md:mb-3'); freshLabel.textContent = 'Start fresh';
  const freshCard = el('button', 'group relative block w-[75vw] flex-1 md:w-full md:flex-none md:aspect-[4/5] rounded-sm overflow-hidden bg-[#FFCA22] cursor-pointer') as HTMLButtonElement;
  freshCard.type = 'button';
  freshCard.innerHTML =
    `<span class="absolute -bottom-[15%] -left-[12%] rotate-30 w-[88%] pointer-events-none">${PAOPAO_WHITE}</span>` +
    `<span class="absolute inset-0 flex items-center justify-center">` +
      `<span class="btn btn--primary btn--animate bg-button text-button-text text-sm px-4 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase inline-flex items-center justify-center">` +
        `<span class="btn__label"><span class="btn__label-inner">New design</span></span>` +
      `</span>` +
    `</span>`;
  freshCard.addEventListener('click', onNewDesign);
  fresh.append(freshLabel, freshCard);

  // ── Recent creations ──
  const recent = el('div', 'min-w-0 min-h-0 flex flex-col h-full');
  const recentLabel = el('div', 'h4 uppercase mb-1.5 md:mb-3'); recentLabel.textContent = 'Your recent creations';
  const scroller = el('div', 'h-full flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-0 snap-x');
  // Custom scrollbar (same 3-piece track+thumb as the reviews section), shown only when
  // the cards overflow the width. Positioned by JS from the scroller's scroll state.
  const scrollbar = el('div', 'relative h-0.75 md:h-1 mt-3 hidden');
  const trackStart = el('div', 'absolute top-0 bottom-0 left-0 bg-foreground/20 rounded-full');
  const thumb = el('div', 'absolute top-0 bottom-0 bg-foreground rounded-full');
  const trackEnd = el('div', 'absolute top-0 right-0 bottom-0 bg-foreground/20 rounded-full');
  scrollbar.append(trackStart, thumb, trackEnd);
  recent.append(recentLabel, scroller, scrollbar);

  function updateScrollbar(): void {
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 1) { scrollbar.classList.add('hidden'); return; } // fits → no scrollbar
    scrollbar.classList.remove('hidden');
    const gap = 6;
    const containerWidth = scrollbar.clientWidth;
    const thumbWidth = (scroller.clientWidth / scroller.scrollWidth) * containerWidth;
    const thumbLeft = (scroller.scrollLeft / maxScroll) * (containerWidth - thumbWidth);
    trackStart.style.width = Math.max(0, thumbLeft - gap) + 'px';
    thumb.style.left = thumbLeft + 'px';
    thumb.style.width = thumbWidth + 'px';
    trackEnd.style.left = (thumbLeft + thumbWidth + gap) + 'px';
  }
  scroller.addEventListener('scroll', updateScrollbar);
  new ResizeObserver(updateScrollbar).observe(scroller);

  function renderCards(): void {
    scroller.innerHTML = '';
    for (const fav of favorites) scroller.appendChild(card(fav));
    if (favorites.length === 0) {
      const empty = el('p', 'p text-foreground/50'); empty.textContent = 'No saved designs yet.';
      scroller.appendChild(empty);
    }
    requestAnimationFrame(updateScrollbar); // layout settled → size the thumb
  }

  function card(fav: SavedBuild): HTMLElement {
    const { addable } = buildAvailability(catalog, fav.build);
    const c = el('div', 'cfg__recent-card group relative shrink-0 flex flex-col w-[75vw] md:w-[24vw] snap-start');

    // Image area: snapshot + info overlaid on top, hover actions wiping in from below,
    // sold-out tag + glass trash. The snapshot nudges up on hover so it stays readable
    // once the buttons appear.
    const media = el('div', 'relative aspect-unset h-full rounded-sm overflow-hidden bg-[#EDEDED]');
    // Background tint behind the snapshot — blue when Edit is hovered, yellow when Add to
    // bag is hovered (mix-blend like the product cards; only the background tints, not the piece).
    const tint = el('span', 'absolute inset-0 mix-blend-color opacity-0 transition-[opacity,background-color] duration-300 ease-out pointer-events-none');
    const setTint = (color: string | null) => {
      if (color) { tint.style.backgroundColor = color; tint.style.opacity = '1'; }
      else tint.style.opacity = '0';
    };
    const img = fav.snapshot
      ? `<img class="absolute inset-0 w-full h-full object-contain p-6 -translate-y-[6%] transition-transform duration-300 ease-out md:p-4 md:translate-y-0 md:group-hover:-translate-y-[7%]" src="${fav.snapshot}" alt="">`
      : `<span class="absolute inset-0 flex items-center justify-center text-foreground/30">${fav.title}</span>`;
    const soldTag = !addable
      ? `<span class="absolute top-2.5 left-2.5 z-[3] product-badge max-w-[55%] whitespace-normal leading-tight">Some charms sold out</span>`
      : '';

    // Overlay pinned to the bottom: info (always shown) + actions (expand in on hover).
    const overlay = el('div', 'absolute inset-x-0 bottom-0 z-[3] p-3');
    const info = el('div', 'flex items-end justify-between gap-3');
    const left = el('div', 'min-w-0');
    const title = el('div', 'h5 m-0 truncate text-foreground'); title.textContent = fav.title;
    const stl = el('div', 'p m-0 text-foreground/70 truncate'); stl.textContent = fav.subtitle;
    left.append(title, stl);
    const price = el('div', `h5 m-0 shrink-0${addable ? '' : ' text-foreground/40'}`); price.textContent = priceLabel(fav.price);
    info.append(left, price);

    // Always visible on mobile (no hover); expands in on hover from md up.
    const actionsWrap = el('div', 'overflow-hidden max-h-20 opacity-100 transition-all duration-500 ease-out md:max-h-0 md:opacity-0 md:group-hover:max-h-20 md:group-hover:opacity-100');
    const actions = el('div', 'flex gap-2 pt-3');
    // Edit — glass button, blue on hover, with the cursor-follow glow.
    const editBtn = el('button', 'btn btn--glass btn--animate relative overflow-hidden flex-1 bg-white/50 text-black text-xs px-3 py-2.5 leading-none rounded-xs font-semibold uppercase inline-flex items-center justify-center backdrop-blur-sm') as HTMLButtonElement;
    editBtn.type = 'button';
    editBtn.style.setProperty('--glow-color', glowColor('#C8D7EA'));
    editBtn.innerHTML = `<span class="btn__label relative z-1"><span class="btn__label-inner">Edit design</span></span>`;
    editBtn.addEventListener('click', () => onEdit(fav));
    editBtn.addEventListener('mouseenter', () => setTint('#C8D7EA')); // blue card background
    actions.appendChild(editBtn);
    if (addable) {
      // Add to bag — primary, yellow on hover (only while enabled), with loading state.
      // While it's loading, pin the actions open so moving the cursor off the card doesn't
      // collapse the hover-reveal and "wipe" the button away mid-load.
      const bagBtn = makeLoadingButton(
        'Add to bag',
        'btn text-xs px-3 py-2.5 leading-none rounded-xs font-semibold uppercase cursor-pointer flex-1 inline-flex items-center justify-center',
        'bg-button text-button-text',
        async () => {
          actionsWrap.style.maxHeight = '5rem'; actionsWrap.style.opacity = '1';
          try { await onAddToBag(fav); }
          finally { actionsWrap.style.maxHeight = ''; actionsWrap.style.opacity = ''; }
        },
      );
      bagBtn.addEventListener('mouseenter', () => setTint('#FFCA22')); // yellow card background
      actions.appendChild(bagBtn);
    }
    actionsWrap.appendChild(actions);
    overlay.append(info, actionsWrap);

    // Delete (top-right, hover) — glass style with glow.
    const del = el('button', 'btn--glass overflow-hidden absolute top-2.5 right-2.5 z-[4] w-8 h-8 inline-flex items-center justify-center rounded-xs bg-white/50 backdrop-blur-sm text-foreground opacity-100 transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100 cursor-pointer') as HTMLButtonElement;
    del.type = 'button'; del.setAttribute('aria-label', 'Delete saved design');
    del.style.setProperty('--glow-color', 'rgba(255,255,255,0.65)');
    del.innerHTML = `<span class="relative z-1 inline-flex [&>svg]:w-5 [&>svg]:h-5">${TRASH}</span>`;
    del.addEventListener('click', async () => {
      await onDelete(fav.id);
      favorites = favorites.filter(f => f.id !== fav.id);
      renderCards();
    });

    media.innerHTML = img + soldTag;
    media.prepend(tint); // behind the snapshot so only the background tints
    media.append(overlay, del);

    // Clear the tint only when leaving the whole card, so moving Edit → Add to bag
    // animates blue → yellow instead of dipping through transparent.
    c.addEventListener('mouseleave', () => setTint(null));
    c.append(media);
    return c;
  }

  renderCards();
  cols.append(fresh, recent);
  wrap.append(copy, cols);
  root.appendChild(wrap);
}
